import Plyr from 'plyr';
import Vimeo from './Vimeo';
import Youtube from './Youtube';

export default class Video {
  constructor(el, options) {
    this.el = el;
    this.options = options;

    if (el.classList.contains('load-plyr')) {
      if (!(this.loadPlyrStyles instanceof Promise)) {
        this.loadPlyrStyles = new Promise((resolve) => {
          const stylesheet = document.createElement('link');
          stylesheet.setAttribute('rel', 'stylesheet');
          stylesheet.href =
            'https://cdn.shopify.com/shopifycloud/shopify-plyr/v1.0/shopify-plyr.css';
          stylesheet.onload = resolve;
          document.body.appendChild(stylesheet);
        });
      }

      this.loadPlyrStyles.then(() => {
        this._setupSelectedVideo();
      });
    } else {
      this._setupSelectedVideo();
    }
  }

  _setupSelectedVideo() {
    this.isDesktop = window.matchMedia(`(min-width: 992px)`).matches;
    this.isMobile = window.matchMedia(`(max-width:  992px)`).matches;

    this.platform = this.el.getAttribute('data-video').trim();
    this.playButton = this.el.querySelector('[data-video-play-button]');

    this.videoEl = this.el.querySelector('[data-video-element]');

    this.onPlayClick = this._onPlayClick.bind(this);
    this.onPauseClick = this._onPauseClick.bind(this);
    this.autoplay = this._autoplay.bind(this);

    this.sources = [];

    this.video = null;
    this.desktop_url = this.videoEl.getAttribute('data-video-url');
    this.mobile_url = this.videoEl.getAttribute('data-mobile-video-url');

    this.desktop_loop = this.el.getAttribute('data-autoloop') === 'true';
    this.desktop_autoplay = this.el.getAttribute('data-autoplay') === 'true';
    this.mobile_loop = this.el.getAttribute('data-mobile-autoloop') === 'true';
    this.mobile_autoplay =
      this.el.getAttribute('data-mobile-autoplay') === 'true';

    this.videoData = {
      el: this.videoEl.childNodes[0],
      videoUrl: this.desktop_url,
      loop: this.desktop_loop,
      autoplay: this.desktop_autoplay,
    };

    if (this.isDesktop) {
      if (this.videoEl.dataset.mp4Video) {
        this.sources.push({
          type: 'video/mp4',
          src: this.videoEl.dataset.mp4Video,
        });
      }
      if (this.videoEl.dataset.ogvVideo) {
        this.sources.push({
          type: 'video/ogv',
          src: this.videoEl.dataset.ogvVideo,
        });
      }
    } else if (this.isMobile && this.el.dataset.mobileVideo) {
      this.videoData.videoUrl = this.mobile_url ?? this.desktop_url;
      this.platform = this.el.dataset.mobileVideo.trim();
      this.videoData.loop = this.mobile_loop ?? this.desktop_loop;
      this.videoData.autoplay = this.mobile_autoplay ?? this.desktop_autoplay;

      if (this.videoEl.dataset.mobileMp4Video) {
        this.sources.push({
          type: 'video/mp4',
          src: this.videoEl.dataset.mobileMp4Video,
        });
      }
      if (this.videoEl.dataset.mobileOgvVideo) {
        this.sources.push({
          type: 'video/ogv',
          src: this.videoEl.dataset.mobileOgvVideo,
        });
      }
    }

    switch (this.platform) {
      case 'youtube':
        this.video = new Youtube(this.videoData);
        break;
      case 'vimeo':
        this.video = new Vimeo(this.videoData);
        break;
      case 'html5':
        this.video = new Plyr(this.videoData.el, {
          loop: { active: this.options && this.options.loop },
          muted: true, //required for autoplay to work on chrome,
          autoplay: this.videoData.autoplay ?? false,
          controls: ['play-large'],
        });
        break;
      default:
        this.video = null;
        break;
    }

    this.el.addEventListener('click', this.onPlayClick);

    if (this.playButton) {
      if (this.isDesktop && this.desktop_autoplay) {
        this.autoplay();
      } else if (this.isMobile && this.mobile_autoplay) {
        this.autoplay();
      }

      this.playButton.addEventListener('click', this.onPlayClick);
    }

    if (this.video && this.platform === 'html5') {
      this.video.source = {
        type: 'video',
        sources: this.sources,
        poster: this.videoData.el.dataset.poster ?? '',
      };
      this.video.on('ready', (event) => {
        setTimeout(() => {
          this.videoEl.classList.add('is-loaded');
        }, 200);
      });
      this.video.on('playing', (event) => {
        this.el.classList.add('video-transitioning');
        this.el.parentNode.classList.add('video-transitioning');

        setTimeout(() => {
          this.el.classList.remove('video-loading');
          this.el.classList.remove('video-transitioning');
          this.el.classList.add('video-playing');

          this.el.parentNode.classList.remove('video-loading');
          this.el.parentNode.classList.remove('video-transitioning');
          this.el.parentNode.classList.add('video-playing');
        }, 200);
      });
    }
  }

  _onPlayClick() {
    this.el.classList.add('video-loading');
    this.el.parentNode.classList.add('video-loading');

    this.video.play().then(() => {
      this.el.classList.add('video-transitioning');
      this.el.parentNode.classList.add('video-transitioning');

      setTimeout(() => {
        this.el.classList.remove('video-loading');
        this.el.classList.remove('video-transitioning');
        this.el.classList.add('video-playing');

        this.el.parentNode.classList.remove('video-loading');
        this.el.parentNode.classList.remove('video-transitioning');
        this.el.parentNode.classList.add('video-playing');
      }, 200);
    });
  }

  _onPauseClick() {
    this.video.pause();
  }

  _autoplay() {
    this.el.classList.add('video-loading');
    this.el.parentNode.classList.add('video-loading');

    if (this.platform !== 'html5') {
      this.video.autoplay().then(() => {
        this.el.classList.add('video-transitioning');
        this.el.parentNode.classList.add('video-transitioning');

        setTimeout(() => {
          this.el.classList.remove('video-loading');
          this.el.classList.remove('video-transitioning');
          this.el.classList.add('video-playing');

          this.el.parentNode.classList.remove('video-loading');
          this.el.parentNode.classList.remove('video-transitioning');
          this.el.parentNode.classList.add('video-playing');
        }, 200);
      });
    }
  }

  play() {
    this._onPlayClick();
  }

  pause() {
    this._onPauseClick();
  }

  unload() {
    this.el.removeEventListener('click', this.onPlayClick);

    if (this.playButton) {
      this.playButton.removeEventListener('click', this.onPlayClick);
    }

    if (this.video) {
      this.video.unload();
    }
  }

  destroy() {
    this.unload();
  }
}
